<template>
  <div class="sp-summary">
    <div class="sp-summary-header ba-accent-header">
      <h2 class="sp-summary-title">Funnel: {{ period }}</h2>
    </div>
    <div class="sp-summary-body">
      <div class="row">
        <div v-for="tile in tiles" :key="tile.label" class="col-6 col-md-3 mb-2">
          <div class="sp-tile">
            <div class="sp-tile-label">{{ tile.label }}</div>
            <div class="sp-tile-value">{{ tile.value }}</div>
            <div v-if="tile.note" class="sp-tile-note" role="status">
              <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>{{ tile.note }}
            </div>
          </div>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-sm sp-funnel mb-0">
          <thead>
            <tr>
              <th scope="col">Stage</th>
              <th scope="col" class="sp-bar-col"><span class="sr-only">Relative to Lead</span></th>
              <th scope="col" class="text-right">Count</th>
              <th scope="col" class="text-right">Value</th>
              <th scope="col" class="text-right">Step %</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in summary.rows" :key="row.key">
              <th scope="row">{{ row.label }}</th>
              <td class="sp-bar-col">
                <div class="sp-bar-track" aria-hidden="true">
                  <div
                    class="sp-bar"
                    :class="{ 'sp-bar-lost': row.key === 'closed_lost' }"
                    :style="{ width: `${row.barPercent}%` }"
                  ></div>
                </div>
              </td>
              <td class="text-right">{{ formatCount(row.count) }}</td>
              <td class="text-right">{{ row.value == null ? "n/a" : formatValue(row.value) }}</td>
              <td class="text-right">{{ row.stepPercent == null ? "" : formatPercent(row.stepPercent) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { formatCount, monthLabel } from "./chartTheme";
import { LOW_VALUE_FILL_RATE, formatPercent, formatValue, funnelSummary } from "./salesPipeline";

/**
 * Sales Pipeline funnel summary: KPI tiles and one row per Funnel Stage, totalled over every
 * Reporting Month in the response (the last 12 full months plus the current one). Shows count and
 * value together, so it ignores the Count / Value toggle.
 */
export default {
  name: "SalesPipelineSummary",
  props: {
    /** The Sales Pipeline API response. */
    funnel: { type: Object, required: true },
  },
  computed: {
    summary() {
      return funnelSummary(this.funnel.months);
    },
    period() {
      const months = this.funnel.months;
      if (!months.length) return "";
      const last = months[months.length - 1];
      return `${monthLabel(months[0].month)} to ${monthLabel(last.month)}${last.partial ? " (so far)" : ""}`;
    },
    /** Says what share of deals had a value, when too few did for value figures to be trusted. */
    valueNote() {
      const rate = this.funnel.valueFillRate;
      if (rate == null || rate >= LOW_VALUE_FILL_RATE) return "";
      return `Only ${formatPercent(rate * 100)} of deals had a value.`;
    },
    tiles() {
      const s = this.summary;
      return [
        { label: "Leads", value: formatCount(s.leads) },
        { label: "Closed won", value: formatCount(s.closed) },
        { label: "Closed value", value: formatValue(s.closedValue), note: this.valueNote },
        { label: "Lead to closed", value: formatPercent(s.leadToClosed, 1) },
      ];
    },
  },
  methods: { formatCount, formatPercent, formatValue },
};
</script>

<style scoped>
.sp-summary {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(33, 47, 63, 0.12);
  margin-bottom: 24px;
}
.sp-summary-header {
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 16px;
}
.sp-summary-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: inherit;
}
.sp-summary-body {
  padding: 16px;
}
.sp-tile {
  height: 100%;
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-radius: 8px;
  padding: 12px;
}
.sp-tile-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.75;
}
.sp-tile-value {
  font-size: 20px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.sp-tile-note {
  font-size: 12px;
  color: #b9770e;
}
.sp-funnel th,
.sp-funnel td {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  vertical-align: middle;
}
.sp-bar-col {
  width: 40%;
  min-width: 120px;
}
.sp-bar-track {
  height: 12px;
  border-radius: 6px;
  background: rgba(128, 128, 128, 0.12);
  overflow: hidden;
}
.sp-bar {
  height: 100%;
  border-radius: 6px;
  background: #6eb644;
}
.sp-bar-lost {
  background: #e34948;
}
</style>

<!-- Unscoped: the theme class is on <body>. Shared with the other Business Analytics cards. -->
<style src="./accentHeader.css"></style>
